"use client";

import { useEffect, useState, type ReactNode } from "react";
import type { Language } from "@/lib/i18n";

const ENTER_MS = 280;
const GLYPHS = "AaBbΔλשאבРуАБعرहिअア01";

function WarpGlyphField({ active }: { active: boolean }) {
    const [glyphs, setGlyphs] = useState<{ id: number; ch: string; x: number; y: number; delay: number }[]>([]);

    useEffect(() => {
        if (!active) {
            setGlyphs([]);
            return;
        }

        const next = Array.from({ length: 10 }, (_, id) => ({
            id,
            ch: GLYPHS[Math.floor(Math.random() * GLYPHS.length)] ?? "·",
            x: 18 + Math.random() * 64,
            y: 28 + Math.random() * 44,
            delay: Math.random() * 0.1,
        }));
        setGlyphs(next);
    }, [active]);

    if (!active || glyphs.length === 0) {
        return null;
    }

    return (
        <div className="locale-glyph-field" aria-hidden>
            {glyphs.map((glyph) => (
                <span
                    key={glyph.id}
                    className="locale-glyph"
                    style={{
                        left: `${glyph.x}%`,
                        top: `${glyph.y}%`,
                        animationDelay: `${glyph.delay}s`,
                    }}
                >
                    {glyph.ch}
                </span>
            ))}
        </div>
    );
}

export function LocaleTransition({ lang, children }: { lang: Language; children: ReactNode }) {
    const [warpActive, setWarpActive] = useState(false);

    useEffect(() => {
        const root = document.documentElement;
        if (root.dataset.localeSwitch !== "exit") {
            return;
        }

        setWarpActive(true);
        root.dataset.localeSwitch = "enter";
        const timer = window.setTimeout(() => {
            delete root.dataset.localeSwitch;
            setWarpActive(false);
        }, ENTER_MS);

        return () => window.clearTimeout(timer);
    }, [lang]);

    useEffect(() => {
        const onExit = () => setWarpActive(true);
        window.addEventListener("scamatrix:locale-exit", onExit);
        return () => window.removeEventListener("scamatrix:locale-exit", onExit);
    }, []);

    return (
        <>
            <div className="locale-shell">{children}</div>
            <WarpGlyphField active={warpActive} />
        </>
    );
}
